/* @HEADER
 * @version 0.15.943 | 2026-07-15
 * @purpose Pointer-driven edge-swipe-to-open / drag-to-close gesture hook for the mobile left nav-drawer (ZVX-DEV-2531). Pure threshold/interpolation math is exported separately so it is unit-testable without DOM/pointer events.
 * @sidecar useEdgeSwipeDrawer.ts.header.md
 * @layer app | @hex _none_ | @ctx _none_
 * @public true
 * @edit careful
 */

// SpecRefs: ZVX-DEV-2531, ZVX-DEV-2337

import { useCallback, useEffect, useRef, useState } from 'react';

/** Edge-detection zone width (px) from the left screen edge — swipe-open only registers here. */
export const EDGE_ZONE_PX = 20;

/** Minimum drag distance (px) to open past the spring-back threshold. */
export const OPEN_DRAG_THRESHOLD_PX = 60;

/** Minimum drag distance (px, i.e. fraction of width) to close — 50% of drawer width. */
export const CLOSE_DRAG_FRACTION = 0.5;

/** Velocity threshold (px/ms) that opens/closes regardless of distance (flick gesture). */
export const VELOCITY_THRESHOLD = 0.3;

/** Drawer width used for scrim-opacity interpolation and close-fraction math. */
export const DRAWER_WIDTH_PX = 360;

/** Peak scrim opacity at full-open. */
export const SCRIM_MAX_OPACITY = 0.4;

/**
 * Given the horizontal drag delta (px, positive = rightward) while OPENING
 * (drawer closed, dragging from the edge zone), decide whether the release
 * should complete the open or spring back.
 *
 * Pure function — no DOM access — unit-testable in isolation.
 */
export function shouldCompleteOpen(dragDeltaX: number, velocityPxPerMs: number): boolean {
  if (dragDeltaX >= OPEN_DRAG_THRESHOLD_PX) return true;
  if (velocityPxPerMs >= VELOCITY_THRESHOLD && dragDeltaX > 0) return true;
  return false;
}

/**
 * Given the horizontal drag delta (px, negative = leftward) while CLOSING
 * (drawer open, dragging from anywhere inside the panel), decide whether the
 * release should complete the close or spring back to open.
 *
 * `dragDeltaX` is negative when dragging left; `width` is the drawer's
 * rendered width in px (used to compute the 50% fraction).
 */
export function shouldCompleteClose(dragDeltaX: number, velocityPxPerMs: number, width: number): boolean {
  const leftward = -dragDeltaX; // positive when moving left
  if (leftward >= width * CLOSE_DRAG_FRACTION) return true;
  if (velocityPxPerMs >= VELOCITY_THRESHOLD && leftward > 0) return true;
  return false;
}

/**
 * Interpolates scrim opacity linearly from 0 (closed) to SCRIM_MAX_OPACITY
 * (fully open) based on progress in [0, 1] (dragDeltaX / width, clamped).
 */
export function scrimOpacityForProgress(progress: number): number {
  const clamped = Math.max(0, Math.min(1, progress));
  return clamped * SCRIM_MAX_OPACITY;
}

/**
 * translateX (px, <= 0) for the drawer panel given a drag delta while opening
 * or closing. `width` is the drawer's rendered width.
 * - Opening: dragDeltaX in [0, width] -> translateX in [-width, 0]
 * - Closing: dragDeltaX in [-width, 0] -> translateX in [-width, 0] (offset from 0, i.e. open)
 */
export function translateXForDrag(dragDeltaX: number, width: number, startedOpen: boolean): number {
  if (startedOpen) {
    // Dragging left from open (translateX starts at 0).
    return Math.max(-width, Math.min(0, dragDeltaX));
  }
  // Dragging right from closed (translateX starts at -width).
  return Math.max(-width, Math.min(0, dragDeltaX - width));
}

interface UseEdgeSwipeDrawerOptions {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** Disable pointer listeners entirely (e.g. non-mobile viewport). */
  enabled: boolean;
  /** Drawer panel width in px, used for close-fraction math. Defaults to DRAWER_WIDTH_PX. */
  width?: number;
}

interface EdgeSwipeDrawerState {
  /** Live drag translateX (px) while a gesture is in progress; null when idle. */
  dragTranslateX: number | null;
  /** Live scrim opacity (0..SCRIM_MAX_OPACITY) while a gesture is in progress; null when idle. */
  dragScrimOpacity: number | null;
  /** True while a pointer gesture is actively tracked. */
  isDragging: boolean;
}

/**
 * Binds document-level pointer listeners to implement:
 * - edge-swipe-open: pointerdown within EDGE_ZONE_PX of the left edge while
 *   closed, drag right past OPEN_DRAG_THRESHOLD_PX or a fast rightward flick.
 * - swipe-to-close: pointerdown anywhere inside the open drawer panel
 *   (caller wires panelRef), drag left past 50% width or a fast leftward flick.
 *
 * Returns live drag state for the caller to apply as inline transform/opacity
 * during an active gesture (falls back to CSS transition classes when idle).
 */
export function useEdgeSwipeDrawer(
  { open, onOpen, onClose, enabled, width = DRAWER_WIDTH_PX }: UseEdgeSwipeDrawerOptions,
): EdgeSwipeDrawerState & { panelRef: React.RefObject<HTMLDivElement | null> } {
  const panelRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<EdgeSwipeDrawerState>({
    dragTranslateX: null,
    dragScrimOpacity: null,
    isDragging: false,
  });

  const gesture = useRef<{
    startX: number;
    startY: number;
    startTime: number;
    startedOpen: boolean;
    active: boolean;
    lockedHorizontal: boolean;
  } | null>(null);

  const resetDrag = useCallback(() => {
    gesture.current = null;
    setState({ dragTranslateX: null, dragScrimOpacity: null, isDragging: false });
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;

      if (!open) {
        // Only start an open-gesture within the left edge zone.
        if (e.clientX > EDGE_ZONE_PX) return;
      } else {
        // Only start a close-gesture inside the drawer panel.
        const panel = panelRef.current;
        if (!panel || !panel.contains(e.target as Node)) return;
      }

      gesture.current = {
        startX: e.clientX,
        startY: e.clientY,
        startTime: performance.now(),
        startedOpen: open,
        active: true,
        lockedHorizontal: false,
      };
    };

    const onPointerMove = (e: PointerEvent) => {
      const g = gesture.current;
      if (!g || !g.active) return;

      const dx = e.clientX - g.startX;
      const dy = e.clientY - g.startY;

      if (!g.lockedHorizontal) {
        // Require a clearly-horizontal intent before hijacking the gesture,
        // so vertical scroll inside the drawer / edge zone stays usable.
        if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          gesture.current = null;
          return;
        }
        g.lockedHorizontal = true;
      }

      const translateX = translateXForDrag(dx, width, g.startedOpen);
      const progress = g.startedOpen
        ? 1 + translateX / width
        : (translateX + width) / width;

      setState({
        dragTranslateX: translateX,
        dragScrimOpacity: scrimOpacityForProgress(progress),
        isDragging: true,
      });
    };

    const finishGesture = (e: PointerEvent) => {
      const g = gesture.current;
      if (!g || !g.active || !g.lockedHorizontal) {
        resetDrag();
        return;
      }

      const dx = e.clientX - g.startX;
      const elapsedMs = Math.max(1, performance.now() - g.startTime);
      const velocity = Math.abs(dx) / elapsedMs;

      if (g.startedOpen) {
        if (shouldCompleteClose(dx, velocity, width)) onClose();
      } else {
        if (shouldCompleteOpen(dx, velocity)) onOpen();
      }

      resetDrag();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', finishGesture);
    document.addEventListener('pointercancel', finishGesture);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', finishGesture);
      document.removeEventListener('pointercancel', finishGesture);
    };
  }, [enabled, open, onOpen, onClose, width, resetDrag]);

  return { ...state, panelRef };
}
